import React, { useState, useEffect, useRef } from 'react';
import CustomSelect from '@/components/common/CustomSelect';
import Button from '@/components/common/Button';
import PatenteDisplay from '@/components/common/PatenteDisplay';
import VehiculoSelector from '@/components/common/VehiculoSelector';
import ValidatedInput from '@/components/common/ValidatedInput';
import { FaCar, FaUser, FaIdCard, FaPalette, FaTachometerAlt, FaCalendarAlt, FaTag, FaSpinner, FaSearch } from 'react-icons/fa';
import { normalizeDataObject } from '@/utils/normalizationUtils';
import { usePlanValidation } from '@/hooks/usePlanValidation';
import useConsultaPatente from '@/hooks/useConsultaPatente';
import { useConfiguracion } from '@/context/ConfiguracionContext';
import QuotaWarning from '@/components/common/QuotaWarning';
import PaymentModal from '@/components/payments/PaymentModal';
import toastService from '@/utils/toastService';

/**
 * Formulario para crear o editar vehículos
 * 
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.initialData - Datos iniciales del vehículo (para edición)
 * @param {Array} props.clientes - Lista de clientes disponibles
 * @param {Array} props.categorias - Lista de categorías/tipos de vehículo disponibles
 * @param {string} props.clienteId - ID del cliente (si viene de la ruta de cliente)
 * @param {boolean} props.isAdmin - Si el usuario es administrador
 * @param {Object} props.clienteData - Datos del cliente actual (solo para clientes)
 * @param {function} props.onSubmit - Función a ejecutar al enviar el formulario
 * @param {function} props.onCancel - Función a ejecutar al cancelar
 * @returns {JSX.Element} - Componente de formulario de vehículo
 */
const VehiculoForm = ({ 
  initialData = {}, 
  clientes = [], 
  categorias = [],
  clienteId = null, 
  isAdmin = false,
  clienteData = null,
  onSubmit, 
  onCancel 
}) => {
  // Hook para validación de cuotas
  const {
    validateQuota,
    getResourceUsage,
    showPaymentModal,
    targetPlanId,
    closePaymentModal,
    openPaymentModal
  } = usePlanValidation();
  
  // Hooks para búsqueda de patente
  const { featureFlags } = useConfiguracion();
  const { consultarPatente, loading: patentLoading, error: patentError } = useConsultaPatente();
  
  const vehiculoSelectorRef = useRef(null);
  const [formData, setFormData] = useState({
    patente: '',
    marca_local: '',
    modelo_local: '',
    marca_global_id: null,
    modelo_global_id: null,
    año: '',
    kilometraje: '',
    categoria_id: '',
    cliente_id: clienteId || (clienteData ? clienteData.id : ''),
    color: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Si hay datos iniciales, actualizar el estado del formulario
  useEffect(() => {
    if (initialData && Object.keys(initialData).length) {
      // Normalizar datos iniciales para tener consistencia
      const normalizedData = normalizeDataObject(initialData, 'vehiculo');
      
      const newFormData = {
        patente: normalizedData.patente || normalizedData.Patente || '',
        marca_local: normalizedData.marca_local || normalizedData.Marca || '',
        modelo_local: normalizedData.modelo_local || normalizedData.Modelo || '',
        marca_global_id: normalizedData.marca_global_id || normalizedData.MarcaGlobalId || null,
        modelo_global_id: normalizedData.modelo_global_id || normalizedData.ModeloGlobalId || null,
        año: normalizedData.año || normalizedData.Año || '',
        kilometraje: normalizedData.kilometraje || normalizedData.Kilometraje || '',
        categoria_id: normalizedData.categoria_id || normalizedData.CategoriaId || '',
        cliente_id: normalizedData.cliente_id || normalizedData.ClienteId || clienteId || (clienteData ? clienteData.id : ''),
        color: normalizedData.color || normalizedData.Color || '',
      };
      
      //console.log("Inicializando formulario con datos:", newFormData);
      setFormData(newFormData);
    } else if (clienteData) {
      // Si es un nuevo formulario pero tenemos datos del cliente
      setFormData(prev => ({
        ...prev,
        cliente_id: clienteId || (clienteData ? clienteData.id : ''),
      }));
    }
  }, [initialData, clienteId, clienteData]);

  // Validar formato de patente chilena
  const validarPatente = (patente) => {
    const regex = /^[A-Z]{2}[0-9]{4}$|^[A-Z]{4}[0-9]{2}$/;
    return regex.test(patente.toUpperCase());
  };

  // Función para consultar patente
  const handleConsultaPatente = async () => {
    if (!formData.patente || !validarPatente(formData.patente)) {
      toastService.warning('Formato de patente inválido. Usa formato ABCD12 o AB1234');
      return;
    }

    const resultado = await consultarPatente(formData.patente.toUpperCase(), 'boostr');
    
    if (resultado.found && resultado.vehiculo) {
      //console.log('🚗 [VehiculoForm] Datos recibidos de la API:', resultado);
      
      // Actualizar los datos del formulario con la información encontrada
      const vehiculoData = resultado.vehiculo;
      setFormData(prevData => ({
        ...prevData,
        marca_local: vehiculoData.marca_local || vehiculoData.marca || prevData.marca_local,
        modelo_local: vehiculoData.modelo_local || vehiculoData.modelo || prevData.modelo_local,
        marca_global_id: vehiculoData.marca_global_id || null,
        modelo_global_id: vehiculoData.modelo_global_id || null,
        año: vehiculoData.año || prevData.año,
        color: vehiculoData.color || prevData.color,
        kilometraje: vehiculoData.kilometraje || prevData.kilometraje
      }));
      
      toastService.success('Datos del vehículo encontrados y cargados automáticamente');
    } else {
      toastService.info('No se encontraron datos para esta patente. Puedes completar la información manualmente.');
    }
  };

  // Manejar cambios en el formulario
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Convertir categoria_id a número si es necesario
    const processedValue = name === 'categoria_id' && value ? parseInt(value, 10) : value;
    
    setFormData(prevData => ({ ...prevData, [name]: processedValue }));
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    // Solo validar cuota para nuevos vehículos (no para edición)
    const isEditing = initialData && initialData.id;
    
    if (!isEditing) {
      // Validar cuota de vehículos antes de crear
      const canCreateVehiculo = validateQuota('vehiculos', {
        showToast: false,
        showModal: true
      });
      
      if (!canCreateVehiculo) {
        //console.log('🚫 No se puede crear vehículo - límite alcanzado');
        return; // Bloquear el envío del formulario
      }
    }
    
    // Forzar validación de VehiculoSelector primero
    let hasErrors = false;
    if (vehiculoSelectorRef.current && !vehiculoSelectorRef.current.validateFields()) {
      hasErrors = true;
    }
    
    // Verificar validación HTML5 del resto del formulario
    const form = e.target;
    if (!form.checkValidity()) {
      hasErrors = true;
      // Disparar los mensajes de validación en todos los campos requeridos
      const invalidElements = form.querySelectorAll(':invalid');
      invalidElements.forEach(el => {
        el.reportValidity();
      });
    }
    
    if (hasErrors) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      let submissionData = {...formData};
      
      // Normalizar datos antes de enviar
      const normalizedSubmissionData = normalizeDataObject(submissionData, 'vehiculo');
      
      await onSubmit(normalizedSubmissionData);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Obtener métricas de uso para vehículos
  const vehiculosUsage = getResourceUsage('vehiculos');
  const isEditing = initialData && initialData.id;

  return (
    <form onSubmit={handleSubmit}>
      {/* Mostrar advertencia de cuota solo para nuevos vehículos */}
      {!isEditing && vehiculosUsage && (
        <QuotaWarning
          resourceType="vehiculos"
          usageMetric={vehiculosUsage}
          onUpgrade={() => openPaymentModal()}
          className="mb-6"
        />
      )}
      {/* Header con icono y descripción */}
      {/* <div className="bg-gradient-to-r from-blue-600 to-green-600 p-4 rounded-lg mb-6">
        <div className="flex items-center space-x-4">
          <div className="bg-white bg-opacity-20 p-3 rounded-full">
            <FaCar className="text-white text-2xl" />
          </div>
          <div>
            <h3 className="text-white text-lg font-semibold">
              {initialData.id ? 'Actualizar Información del Vehículo' : 'Nuevo Vehículo'}
            </h3>
            <p className="text-blue-100 text-sm">
              Complete la información del vehículo para gestionar sus servicios
            </p>
          </div>
        </div>
      </div> */}

      <div className="md:grid md:grid-cols-2 md:gap-6">
        {/* Columna izquierda: Información Básica */}
        <div className="bg-gray-800 p-4 rounded-lg mb-6 md:mb-0 border border-gray-600">
          <div className="flex items-center mb-4 pb-2 border-b border-gray-600">
            <FaIdCard className="text-blue-400 mr-2" />
            <h4 className="text-white font-medium">Información Básica</h4>
          </div>

          {/* Selector de cliente (solo visible para administradores) */}
          {isAdmin && (
            <div className="mb-4">
              <label className="flex items-center text-sm font-medium mb-2">
                <FaUser className="text-gray-400 mr-2 text-xs" />
                Cliente<span className="text-red-500 ml-1">*</span>
              </label>
              <CustomSelect
                name="cliente_id"
                value={formData.cliente_id}
                onChange={handleChange}
                placeholder="Seleccionar cliente"
                options={clientes.map(cliente => ({
                  id: cliente.id,
                  value: cliente.id,
                  label: `${cliente.Nombre} - ${cliente.Telefono || 'Sin teléfono'}`
                }))}
                className="w-full"
                required
                requiredMessage="Debe seleccionar un cliente"
              />
            </div>
          )}
          
          {/* Para usuarios no-admin, solo campos ocultos */}
          {!isAdmin && (
            <>
              <input type="hidden" name="Cliente" value={formData.Cliente} />
              <input type="hidden" name="ClienteTelefono" value={formData.ClienteTelefono} />
            </>
          )}
          
          {/* Patente con Preview y Búsqueda */}
          <div className="mb-4">
            <label className="flex items-center text-sm font-medium mb-2">
              <FaIdCard className="text-gray-400 mr-2 text-xs" />
              Patente<span className="text-red-500 ml-1">*</span>
            </label>
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              {/* Campo de entrada con botón al lado */}
              <div className="flex-1 flex items-center gap-2">
                <div className="flex-1">
                  <ValidatedInput
                    type="text"
                    name="patente"
                    placeholder="Ej: ABC123"
                    value={formData.patente}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase();
                      if (value.length <= 7) {
                        setFormData(prevData => ({ ...prevData, patente: value }));
                      }
                    }}
                    maxLength={7}
                    required
                    className="h-10 w-full"
                  />
                </div>
                {featureFlags?.enablePatentLookup !== false && (
                  <button
                    type="button"
                    onClick={handleConsultaPatente}
                    disabled={patentLoading || !formData.patente || formData.patente.length < 6}
                    className="h-10 px-4 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded transition-colors whitespace-nowrap flex items-center justify-center"
                  >
                    {patentLoading ? (
                      <FaSpinner className="animate-spin mr-1" />
                    ) : (
                      <>
                        <FaSearch className="mr-1" />
                        Buscar
                      </>
                    )}
                  </button>
                )}
              </div>
              
              {/* Preview de la patente en la misma fila */}
              {formData.patente && formData.patente.length >= 3 && (
                <div className="flex items-center justify-center w-full md:w-auto h-14 px-3 bg-gray-900 rounded-lg border border-gray-600">
                  <PatenteDisplay patente={formData.patente} size="sm" className="transform scale-90" />
                </div>
              )}
            </div>
            
            {/* Textos de ayuda y error debajo del input */}
            <div className="mt-2 space-y-1">
              {/* Error de búsqueda */}
              {patentError && (
                <p className="text-red-400 text-sm">{patentError}</p>
              )}
              
              {/* Mensaje de ayuda */}
              {featureFlags?.enablePatentLookup !== false && (
                <p className="text-gray-400 text-sm">
                  Ingresa la patente y haz clic en "Buscar" para autocompletar los datos del vehículo
                </p>
              )}
            </div>
          </div>
          
          {/* Selector de Marca y Modelo */}
          <VehiculoSelector
            ref={vehiculoSelectorRef}
            marcaValue={formData.marca_local}
            modeloValue={formData.modelo_local}
            marcaId={formData.marca_global_id}
            modeloId={formData.modelo_global_id}
            onMarcaChange={(e) => {
              const value = e.target.value;
              const marca = e.target.marca || null;
              setFormData(prevData => ({
                ...prevData,
                marca_local: value,
                marca_global_id: marca?.id || null,
                // Limpiar modelo cuando cambia la marca
                modelo_local: '',
                modelo_global_id: null
              }));
            }}
            onModeloChange={(e) => {
              const value = e.target.value;
              const modelo = e.target.modelo || null;
              setFormData(prevData => ({
                ...prevData,
                modelo_local: value,
                modelo_global_id: modelo?.id || null
              }));
            }}
            required={true}
            allowManualEntry={true}
            className="mb-4"
          />
        </div>
        
        {/* Columna derecha: Detalles del Vehículo */}
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-600">
          <div className="flex items-center mb-4 pb-2 border-b border-gray-600">
            <FaCar className="text-green-400 mr-2" />
            <h4 className="text-white font-medium">Detalles del Vehículo</h4>
          </div>
          
          {/* Año y Color en la misma fila */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Año */}
            <div>
              <label className="flex items-center text-sm font-medium mb-2">
                <FaCalendarAlt className="text-gray-400 mr-2 text-xs" />
                Año<span className="text-red-500 ml-1">*</span>
              </label>
              <ValidatedInput
                type="number"
                name="año"
                placeholder="Ej: 2020"
                value={formData.año}
                onChange={(e) => {
                  const value = e.target.value;
                  const numValue = parseInt(value);
                  const currentYear = new Date().getFullYear();
                  
                  // Permitir campo vacío
                  if (value === '') {
                    setFormData(prevData => ({ ...prevData, año: '' }));
                    return;
                  }
                  
                  // Permitir escribir números de hasta 4 dígitos
                  if (value.length <= 4) {
                    // Si es un número completo de 4 dígitos, validar rango
                    if (value.length === 4) {
                      if (numValue >= 1900 && numValue <= currentYear + 1) {
                        setFormData(prevData => ({ ...prevData, año: value }));
                      }
                    } else {
                      // Permitir escribir mientras construye el número (1, 19, 190, etc.)
                      setFormData(prevData => ({ ...prevData, año: value }));
                    }
                  }
                }}
                required
                min="1900"
                max={new Date().getFullYear() + 1}
              />
            </div>
            
            {/* Color */}
            <div>
              <label className="flex items-center text-sm font-medium mb-2">
                <FaPalette className="text-gray-400 mr-2 text-xs" />
                Color
              </label>
              <ValidatedInput
                type="text"
                name="color"
                placeholder="Ej: Blanco"
                value={formData.color}
                onChange={handleChange}
              />
            </div>
          </div>
          
          {/* Kilometraje */}
          <div className="mb-4">
            <label className="flex items-center text-sm font-medium mb-2">
              <FaTachometerAlt className="text-gray-400 mr-2 text-xs" />
              Kilometraje
            </label>
            <ValidatedInput
              type="number"
              name="kilometraje"
              placeholder="Ej: 50000"
              value={formData.kilometraje}
              onChange={handleChange}
              min="0"
              step="1"
            />
          </div>
          
          {/* Tipo de Vehículo */}
          <div className="border border-gray-600 bg-gray-800 p-4 rounded-lg">
            <div className="flex items-center mb-3">
              <FaTag className="text-purple-400 mr-2" />
              <h5 className="text-white font-medium">Categoría</h5>
            </div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Tipo de Vehículo
            </label>
            <CustomSelect
              name="categoria_id"
              value={formData.categoria_id}
              onChange={handleChange}
              placeholder="Seleccionar tipo de vehículo"
              options={categorias.map(cat => ({
                id: cat.id,
                value: cat.id,
                label: cat.nombre
              }))}
              className="w-full"
            />
          </div>
        </div>
      </div>
      
      <div className="border-t border-gray-700 pt-4 mt-6">
        <div className="flex flex-col md:flex-row gap-3 md:justify-end">
          <Button 
            variant="secondary" 
            onClick={onCancel}
            className="w-full md:w-auto py-2.5 md:px-6 text-sm font-medium order-1"
          >
            Cancelar
          </Button>
          <Button 
            variant="primary"
            type="button"
            onClick={(e) => {
              // Crear un evento sintético para simular submit
              const form = e.target.closest('form');
              const syntheticEvent = { 
                preventDefault: () => {}, 
                target: form 
              };
              handleSubmit(syntheticEvent);
            }}
            loading={isSubmitting}
            disabled={isSubmitting}
            className="w-full md:w-auto py-2.5 md:px-6 text-sm font-medium order-2"
          >
            {initialData.id ? 'Actualizar Vehículo' : 'Guardar Vehículo'}
          </Button>
        </div>
      </div>
      
      {/* Modal de pago para upgrade de plan */}
      <PaymentModal 
        isOpen={showPaymentModal}
        onClose={closePaymentModal}
        targetPlanId={targetPlanId}
      />
    </form>
  );
};

export default VehiculoForm;